import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Clock, FileCheck2 } from "lucide-react";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

const Upload = ({ label, value, onDone, testId }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kyc/upload", fd);
      onDone(data.path);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      <label className="border border-white/20 px-4 py-3 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer" data-testid={`${testId}-label`}>
        {busy ? "Uploading…" : value ? `Replace ${label}` : `Upload ${label}`}
        <input data-testid={`${testId}-input`} type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
      </label>
      {value && <img src={imgUrl(value)} alt={label} className="w-14 h-10 object-cover border border-emerald-400/40" data-testid={`${testId}-preview`} />}
    </div>
  );
};

const STATUS = {
  none: { icon: ShieldAlert, cls: "text-yellow-400 border-yellow-400/40", text: "KYC required before your first payout" },
  pending: { icon: Clock, cls: "text-[#D4AF37] border-[#D4AF37]/40", text: "KYC under review — usually within 24 hours" },
  verified: { icon: ShieldCheck, cls: "text-emerald-400 border-emerald-400/40", text: "KYC verified — payouts enabled" },
  rejected: { icon: ShieldAlert, cls: "text-red-400 border-red-400/40", text: "KYC rejected — please re-upload clear documents" },
};

export const KycSection = () => {
  const [kyc, setKyc] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ pan_number: "", aadhaar_last4: "", pan_image: "", aadhaar_image: "" });

  useEffect(() => {
    api.get("/kyc/me").then((r) => {
      setKyc(r.data);
      setForm((f) => ({ ...f, pan_number: r.data.pan_number || "", aadhaar_last4: r.data.aadhaar_last4 || "", pan_image: r.data.pan_image || "", aadhaar_image: r.data.aadhaar_image || "" }));
    }).catch(() => setKyc({ status: "none" }));
  }, []);

  if (!kyc) return null;
  const s = STATUS[kyc.status] || STATUS.none;
  const Icon = s.icon;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pan_image || !form.aadhaar_image) {
      toast.error("Please upload both PAN and Aadhaar images");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/kyc", form);
      setKyc(data);
      setOpen(false);
      toast.success("KYC submitted for verification");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`mt-6 border bg-[#0A0A0A] ${s.cls.split(" ")[1]}`} data-testid="kyc-section">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
        <div className="flex items-center gap-3">
          <Icon size={16} className={s.cls.split(" ")[0]} />
          <div>
            <p className="text-sm text-white" data-testid="kyc-status-text">{s.text}</p>
            <p className="text-xs text-white/40" data-testid="kyc-status-detail">
              {kyc.status === "none" ? "Upload PAN card and Aadhaar card images once." : `PAN ${kyc.pan_number} · Aadhaar ····${kyc.aadhaar_last4}`}
              {kyc.note ? ` · Note: ${kyc.note}` : ""}
            </p>
          </div>
        </div>
        {kyc.status !== "verified" && (
          <button data-testid="kyc-toggle-button" onClick={() => setOpen((o) => !o)} className="border border-white/20 px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            {open ? "Close" : kyc.status === "none" ? "Complete KYC" : "Update Documents"}
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="px-6 pb-6 grid lg:grid-cols-2 gap-8 border-t border-white/10 pt-6" data-testid="kyc-form">
          <div className="space-y-4">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">PAN card</p>
            <input data-testid="kyc-pan-input" required placeholder="PAN number (ABCDE1234F)" maxLength={10} value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} className={`${inputCls} uppercase tracking-widest`} />
            <Upload label="PAN image" value={form.pan_image} onDone={(p) => setForm({ ...form, pan_image: p })} testId="kyc-pan-upload" />
          </div>
          <div className="space-y-4">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Aadhaar card</p>
            <input data-testid="kyc-aadhaar-input" required placeholder="Last 4 digits of Aadhaar" maxLength={4} value={form.aadhaar_last4} onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g, "") })} className={inputCls} />
            <Upload label="Aadhaar image" value={form.aadhaar_image} onDone={(p) => setForm({ ...form, aadhaar_image: p })} testId="kyc-aadhaar-upload" />
            <button data-testid="kyc-submit-button" disabled={saving} className="w-full flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
              <FileCheck2 size={13} /> {saving ? "Submitting…" : "Submit for Verification"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};
